import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, LayersIcon, ChevronDownIcon } from 'lucide-react';
import { AllAmericanGutterQuoteForm } from './AllAmericanGutterQuoteForm';
interface AllAmericanGutterLandingPageProps {
  initialProject?: string;
  onClose: () => void;
}
export function AllAmericanGutterLandingPage({
  initialProject,
  onClose
}: AllAmericanGutterLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>(initialProject || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialProject) {
      setSelectedProject(initialProject);
    }
  }, [initialProject]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedProject) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <AllAmericanGutterQuoteForm initialZip={zipCode} projectType={selectedProject} onClose={onClose} data-id="element-209" />;
  }
  const projects = [{
    id: 'gutters',
    name: 'New Gutters',
    icon: <LayersIcon className="w-6 h-6" data-id="element-210" />,
    desc: 'Complete gutter replacement'
  }, {
    id: 'protection',
    name: 'Gutter Protection',
    icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-211" />,
    desc: 'Keep leaves and debris out'
  }, {
    id: 'both',
    name: 'Both (Gutters + Protection)',
    icon: <StarIcon className="w-6 h-6" data-id="element-212" />,
    desc: 'Ultimate gutter solution'
  }];
  const projectLabel = projects.find(p => p.id === selectedProject)?.name || '';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-213">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-center items-center shadow-sm relative z-20" data-id="element-214">
        <div className="flex flex-col items-center" data-id="element-215">
          <div className="flex items-baseline gap-2" data-id="element-216">
            <img src="https://cdn.magicpatterns.com/uploads/uf1Vk4MkCjKVeK9UQc7vGE/Guggere_image.webp" alt="All American Gutter Protection" className="h-14 md:h-16 object-contain" data-id="element-217" />
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-218">
            Gutter Guards &bull; Gutter Protection &bull; New Gutters
          </span>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="bg-[#4B7B3F] py-4 md:py-6 border-t border-[#3a5e30]" data-id="element-219">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-220">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-221">
            THE BEST GUTTER PROTECTION FOR YOUR HOME.
          </h2>
          <p className="text-green-100 text-sm mt-1" data-id="element-222">
            Celebrating 10 Years of Excellence — Veteran Owned & Operated
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-223">
        <div className="absolute inset-0 z-0" data-id="element-224">
          <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80" alt="Home exterior with gutters" className="w-full h-full object-cover" data-id="element-225" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-226" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-227">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-228">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-229">
              <h3 className="text-sm md:text-base font-bold text-[#8bc53f] uppercase tracking-wider mb-2" data-id="element-230">
                Gutter Protection Solutions
              </h3>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-231">
                End Gutter Cleaning. Forever.
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-232">
                Gutter guards & lifetime clog-free gutter protection, from our
                family to yours. Schedule your free estimate today.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-233">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-234">
                <h2 className="text-xl font-bold text-[#4B7B3F] text-center mb-6" data-id="element-235">
                  {initialProject ? 'Enter Your Zip Code To Start' : 'Select Project & Enter Your Zip'}
                </h2>

                {/* Project dropdown */}
                {!initialProject && <div className="mb-6 relative" ref={dropdownRef} data-id="element-236">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-green-50 ${selectedProject ? 'border-[#4B7B3F]' : 'border-green-200'} ${dropdownOpen ? 'border-[#4B7B3F] shadow-md' : 'hover:border-green-300'}`} data-id="element-237">
                      <div className="flex items-center gap-3" data-id="element-238">
                        {selectedProject ? <>
                            <span className="text-[#4B7B3F]" data-id="element-239">
                              {projects.find(p => p.id === selectedProject)?.icon}
                            </span>
                            <span className="font-bold text-[#4B7B3F] text-lg" data-id="element-240">
                              {projects.find(p => p.id === selectedProject)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-241">
                            Choose project type...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#4B7B3F] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-242" />
                    </button>

                    <AnimatePresence data-id="element-243">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-green-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-244">
                          {projects.map(proj => <button key={proj.id} onClick={() => {
                      setSelectedProject(proj.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedProject === proj.id ? 'bg-green-50 text-[#4B7B3F]' : 'bg-white text-gray-700 hover:bg-green-50 hover:text-[#4B7B3F]'} border-b border-green-100 last:border-b-0`} data-id="element-245">
                              <span className={`${selectedProject === proj.id ? 'text-[#4B7B3F]' : 'text-gray-400'}`} data-id="element-246">
                                {proj.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-247">
                                {proj.name}
                              </span>
                              {selectedProject === proj.id && <CheckCircle2Icon className="w-5 h-5 text-[#4B7B3F] ml-auto" data-id="element-248" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected project badge when pre-selected */}
                {initialProject && <div className="flex items-center justify-center gap-2 mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5" data-id="element-249">
                    {projects.find(p => p.id === initialProject)?.icon}
                    <span className="font-bold text-[#4B7B3F]" data-id="element-250">
                      {projectLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-251">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#4B7B3F] focus:ring-1 focus:ring-[#4B7B3F] transition-colors" maxLength={5} data-id="element-252" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-253">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-254" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedProject} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedProject ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-255">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-256">
                  <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200" data-id="element-257">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600" data-id="element-258" />
                    <span className="text-xs font-bold text-green-800 uppercase tracking-tight" data-id="element-259">
                      Lifetime Warranty
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-260">
                    <div className="font-black text-blue-700 text-sm" data-id="element-261">BBB</div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-262">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Green Accent Bar */}
      <div className="w-full h-3 bg-[#4B7B3F]" data-id="element-263" />

      {/* Why All American Section */}
      <section className="py-20 bg-white border-b border-gray-100" data-id="element-264">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-265">
          <div className="text-center mb-16" data-id="element-266">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-267">
              Why Choose All American Gutter Protection?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-268">
            {[{
            title: 'Veteran Owned & Operated',
            desc: 'Founded by veterans with a commitment to service, integrity, and excellence in every project.'
          }, {
            title: 'Lifetime Clog-Free Guarantee',
            desc: "Our gutter protection systems come with a lifetime guarantee — if your gutters clog, we'll clean them for free."
          }, {
            title: 'Stainless Steel Micro-Mesh',
            desc: 'Premium 316 stainless steel micro-mesh technology that blocks even the smallest debris while allowing maximum water flow.'
          }, {
            title: 'Professional Installation',
            desc: 'Factory-trained installers ensure your gutter protection is installed correctly the first time, every time.'
          }].map((feature, idx) => <div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-[#4B7B3F] hover:shadow-md transition-shadow" data-id="element-269">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-270">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-271">{feature.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-gray-50" data-id="element-272">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-273">
          <div className="text-center mb-16" data-id="element-274">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-275">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-276">
            {[{
            title: 'Choose Your Project',
            desc: 'Whether you need new seamless gutters, gutter protection, or a complete gutter system — All American has the solution to protect your home from water damage.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }, {
            title: 'Schedule Your Free Estimate',
            desc: 'One of our gutter specialists will visit your home, inspect your current gutters, and provide a detailed, no-obligation quote tailored to your needs.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Expert Installation',
            desc: 'Our trained professionals install your gutter system with precision. Every installation is backed by our lifetime clog-free guarantee for total peace of mind.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-277">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-278">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-279" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-280">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-281">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4B7B3F] py-16" data-id="element-282">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-283">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-284">
            <div className="flex flex-col gap-8" data-id="element-285">
              <div className="flex flex-col" data-id="element-286">
                <img src="https://cdn.magicpatterns.com/uploads/uf1Vk4MkCjKVeK9UQc7vGE/Guggere_image.webp" alt="All American Gutter Protection" className="h-10 brightness-0 invert object-contain object-left mb-2" data-id="element-287" />
                <span className="text-[10px] font-semibold text-white/90" data-id="element-288">
                  Gutter Guards &bull; Gutter Protection &bull; New Gutters
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-289">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-290">
              <div className="flex flex-col gap-4" data-id="element-291">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-292">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-293">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-294">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-295">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-296">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-297">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-298">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-299">
              All American Gutter Protection services are available nationwide.
              All estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}